import { useState } from 'react'

// Mirror of backend MINI_PRESET_SPEC - must stay in sync with
// docs/api/mini-exam-contract.md §2 and the route handler.
export type MiniPreset = 'quick' | 'standard' | 'long'

interface PresetOption {
  key: MiniPreset
  label: string       // "Rápida" / "Padrão" / "Longa"
  questions: number
  minutes: number
}

const PRESETS: PresetOption[] = [
  { key: 'quick',    label: 'Rápida', questions: 10, minutes: 15 },
  { key: 'standard', label: 'Padrão', questions: 20, minutes: 30 },
  { key: 'long',     label: 'Longa',  questions: 30, minutes: 45 },
]

interface Props {
  open: boolean
  subjectName: string
  onClose: () => void
  onConfirm: (preset: MiniPreset) => void
  lastSessionHint?: string | null  // e.g. "Última Padrão: 17/20 em 23min"
}

// Bottom-sheet picker for the mini-exam preset. PT-Moçambique copy.
// Used from SubjectSelectPage when the student picks Mini mode.
export function MiniPresetSheet({ open, subjectName, onClose, onConfirm, lastSessionHint }: Props) {
  const [selected, setSelected] = useState<MiniPreset>('standard')

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-xl bg-white p-5 pb-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300" />

        <h2 className="text-lg font-semibold text-gray-900">
          Mini Exame · {subjectName}
        </h2>
        <p className="mt-1 text-sm text-gray-500">Escolhe a duração.</p>

        <div className="mt-4 space-y-2">
          {/* MP polish (2026-05-24). Selected-state colour shifted from
              indigo-600 → blue-600 so the sheet matches the brand
              primary used everywhere else (StudentTabBar T1, modal CTAs,
              QuestionCard selected option). Radio accent + bottom CTA
              follow. */}
          {PRESETS.map((p) => (
            <label
              key={p.key}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                selected === p.key
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="mini-preset"
                value={p.key}
                checked={selected === p.key}
                onChange={() => setSelected(p.key)}
                className="h-4 w-4 accent-blue-600"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{p.label}</div>
                <div className="text-xs text-gray-500">
                  {p.questions} perguntas · {p.minutes} min
                </div>
              </div>
            </label>
          ))}
        </div>

        {lastSessionHint && (
          <p className="mt-3 text-xs text-gray-500">{lastSessionHint}</p>
        )}

        <div className="mt-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(selected)}
            className="flex-1 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Começar →
          </button>
        </div>
      </div>
    </div>
  )
}
